import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { strings, getCurrentLocale } from 'APP_ROOT/localization';
import { Chart, Axis, Tooltip, Geom, Coord, Legend } from "bizcharts";
import MyQueryRenderer from "../MyQueryRenderer";
import { csvFormat } from 'd3-dsv';
import moment from 'moment';
import ReactTable from 'react-table'; /* for the report tables */
import 'react-table/react-table.css';

import { requestProfilesForIdentities } from 'APP_ROOT/data/identities/actions';
import ProfileImage from 'APP_ROOT/components/ProfileImage';
import ErrorMessage from 'APP_ROOT/components/ErrorMessage';

import MaintenanceMessage from '../MaintenanceMessage';

const DATE_COLUMN = {
    Header: strings.reports_header_date,
    id: 'date',
    accessor: 'date',
    maxWidth: 120,
    Cell: x => <a href={x.original._historyUrlOverride || `/${getCurrentLocale()}/stats/history/${x.original.ravenUuid}/${x.original.cardId}`} target="_blank"> { x.value ? moment(x.value).local().format('MMM Do YYYY') : '-' } </a>,
    aggregate: vals => {
        if(vals.length <= 0)
        {
            return null;
        }

        var min;
        var max;

        if(Array.isArray(vals[0]))
        {
            min = vals[0][0];
            max = vals[0][1];
        }
        else
        {
            min = vals[0];
            max = vals[0];
        }
        vals.forEach(x => {
            if(Array.isArray(x))
            {
                if(min > x[0]) min = x[0];
                if(max < x[1]) max = x[1];
            }
            else
            {
                if(min > x) min = x;
                if(max < x) max = x;
            }
        });
        if(min === max)
            return min;
        return [min, max];
    },
    Aggregated: (x) => (
        x.value ? (
            x.value instanceof Array ?
            (<div>{moment(x.value[0]).local().format('MMM Do YYYY')} - { moment(x.value[1]).local().format('MMM Do YYYY') }</div>)
            : (<div>{moment(x.value).local().format('MMM Do YYYY')}</div>)
        ) : <div>-</div>
    ),
};

const GROUPS = {
    vehicle: [
        {
            fieldName: 'DimVehicle.name',
            tableColumnHeader: strings.reports_header_vehicle,
            ifNull: strings.unknown,
        }
    ],
    driver: [
        {
            fieldName: 'DimDriver.name',
            additionalFieldName: 'DimDriver.identityId',
            tableColumnHeader: strings.reports_header_driver,
            ifNull: strings.unknown,
        },
    ],
    "vehicle,driver": [
        {
            fieldName: 'DimVehicle.name',
            tableColumnHeader: strings.reports_header_vehicle,
            ifNull: strings.unknown,
        },
        {
            fieldName: 'DimDriver.name',
            additionalFieldName: 'DimDriver.identityId',
            tableColumnHeader: strings.reports_header_driver,
            ifNull: strings.unknown,
        },
    ],
    "driver,vehicle": [
        {
            fieldName: 'DimDriver.name',
            additionalFieldName: 'DimDriver.identityId',
            tableColumnHeader: strings.reports_header_driver,
            ifNull: strings.unknown,
        },
        {
            fieldName: 'DimVehicle.name',
            tableColumnHeader: strings.reports_header_vehicle,
            ifNull: strings.unknown,
        },
    ]
}

class ReportsSection extends React.PureComponent {

    cardTypeFilterValues = () => {
        switch(this.props.showDrivingParking) {
            case 'trip':
                return ['trip'];
            case 'parked':
                return ['parked'];
            case 'both':
            default:
               return null;
        }
    }

    cardTypeFilter = () => {
        const filterValues = this.cardTypeFilterValues();
        return filterValues ? {
            "dimension": "DimCard.type",
            "operator": "equals",
            "values": filterValues
        } : null;
    }

    eventTypeFilter = () => {
        return {
            "dimension": "DimEventType.name",
            "operator": "equals",
            "values": document.getEventListForContext("reports")
        };
    }

    render() {
        const group1 = GROUPS[this.props.groupBy];
        const {limitTo, limitToFilteredResources} = this.props;

        var dimensionSet = new Set();

        group1.forEach((x) => {
            dimensionSet.add(x.fieldName);
            if(x.additionalFieldName)
                dimensionSet.add(x.additionalFieldName);
        });

        const filters = [
            this.cardTypeFilter(),
            this.eventTypeFilter()
        ].filter(it=>it); // drop null filters

        return (
            <MyQueryRenderer
                user={this.props.user}
                cubejsApi={this.props.cubejsApi}
                query={{
                    "timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
                    "measures": [
                        "FactEvent.count"
                    ],
                    "timeDimensions": [
                        {
                            "dimension": "FactEvent.timestamp",
                            "granularity": "day",
                            "dateRange": [
                                moment(this.props.selectedDateRange[0]).format('YYYY-MM-DD'),
                                moment(this.props.selectedDateRange[1]).format('YYYY-MM-DD'),
                            ]
                        }
                    ],
                    filters,
                    "dimensions": [
                        ...dimensionSet,
                        "DimEventType.name",
                        "DimRaven.uuid",
                        "DimCard.cardId",
                    ]
                }}
                render={this.renderChart(ConnectedEventTable, this.props.user.stage, { group1, limitTo, limitToFilteredResources })}
            />
        );
    }

    renderChart = (Component, stage, { group1, limitTo, limitToFilteredResources }) => ({ resultSet, error, ...otherProps }) => {
        if (error) {
            return this.renderError(error);
        } else if(resultSet) {
            return <Component 
                        {...otherProps} 
                        stage={stage} 
                        group1={group1} 
                        resultSet={resultSet}
                        limitTo={limitTo}
                        limitToFilteredResources={limitToFilteredResources}
                    />;
        } else {
            return <div>{strings.loading}</div>;
        }
    }

    renderError = error => {
        if(error.message && error.message.match(/maintenance/i)) {
            return <MaintenanceMessage message={error.message} />;
        } else {
            return <ErrorMessage error={error} />;
        }
    }
}

class EventTable extends React.PureComponent {

    componentDidMount() {
        this.fetchIdentities();
    }

    componentDidUpdate() {
        this.fetchIdentities();
    }

    fetchIdentities = () => {
        const identityIds = new Set();
        const rows = this.props.resultSet.rawData();
        rows.forEach(r => {
            if (r['DimDriver.identityId']) identityIds.add(r['DimDriver.identityId']);
        });
        this.props.dispatch(requestProfilesForIdentities(this.props.stage, Array.from(identityIds)));
    }

    getIdentityId = row => {

        if (row['DimDriver.identityId']) {
            return row['DimDriver.identityId'];
        }
        if(row._original)
            if (row._original['DimDriver.identityId']) {
                return row._original['DimDriver.identityId'];
            }
        if (row._subRows && row._subRows[0]) {
            return this.getIdentityId(row._subRows[0]);
        }
        return null;
    }

    stackedVehicleEventData = (group1, eventNames, rows) => {

        // Accumulate the data based on group1 only (collapse the days)

        // Bizcharts wants each distinct piece of data as separate elements in an array
        // Generate the array from the composite data generated above

        var results = {};
        var finalresult = [];

        var finalgroups = new Set();

        rows.forEach( (row) => {

            var groupings = [];

            group1.forEach( (group) => {
                groupings.push(row[group.fieldName]);
            });

            var groupName = groupings.join(',');

            finalgroups.add(groupName);

            eventNames.forEach( (evt) => {
                if(evt in row)
                {
                    if(!results[groupName])
                    {
                        results[groupName] = {};
                    }
                    if(!results[groupName][evt])
                    {
                        results[groupName][evt] = 0;
                    }

                    results[groupName][evt] += row[evt];
                }
            });

        });

        finalgroups.forEach( (groupName) => {
            if(!(groupName in results))
            {
                return;
            }

            eventNames.forEach( (evt) => {
                if(evt in results[groupName])
                {
                    finalresult.push({
                        x: groupName,
                        color: document.getEventName({ type: evt }),
                        measure: results[groupName][evt],
                    });
                }
            });
        });

        return finalresult;
    };

    generateExportURI = (columns, rawRows) => {
        const outputRows = rawRows.map(row => {
            const outRow = {};
            columns.forEach(col => {
                if(col.show || col.show === undefined) {
                    outRow[col.Header] = (typeof col.accessor === 'string' ? row[col.accessor] : col.accessor(row));
                }
            });
            return outRow;
        });
        const BOM='\uFEFF';
        return `data:text/csv;charset=utf-8,${BOM}${encodeURIComponent(csvFormat(outputRows))}`;
    }

    render() {
        const {resultSet, stage, group1 } = this.props;

        return this.tableRender(group1, resultSet, stage);

    }

    tableRender = (group1, resultSet, stage) => {
        const distinctList = new Set();

        const filteredRawData = this.props.limitToFilteredResources(resultSet.rawData(), this.props.limitTo);

        filteredRawData.forEach(row=>{
            distinctList.add(row['DimEventType.name']);
        });

        const eventNames = Array.from(distinctList).filter(name => document.filterEventTypeAllowed(name));

        const columns = [
            ...(group1.map((x) => {
                if(x['tableColumnHeader'] === 'Driver')
                {
                    return {
                        Header: x.tableColumnHeader,
                        accessor: (y) => y[x.fieldName],
                        id: x.fieldName,
                        maxWidth: 220,
                        Pivot: cellInfo => {
                            const identityId = this.getIdentityId(cellInfo.row);
                            const name = cellInfo.value;
                            const classes = cellInfo.isExpanded === undefined || cellInfo.isExpanded === false ? "rt-expander" : "rt-expander -open";
                            // TODO: no need to replace name conditionally with strings.unknown here if we group by identityId

                            return (
                                <ProfileImage
                                    identityId={identityId}
                                    caption={name.startsWith(strings.unknown) ? strings.unknown : name}
                                    stage={stage}
                                    imgSize='96px'
                                    expander={true}
                                    expanderClass={classes}
                                />
                            );
                        }
                    };
                }
                else
                {
                    return { Header: x.tableColumnHeader, accessor: (y) => y[x.fieldName], id: x.fieldName };
                }
            }

            )),
            DATE_COLUMN
        ].concat(
            eventNames
            .map(name => (
                {
                    Header: document.getEventName({ type: name }),
                    accessor: name,
                    aggregate: vals => { return vals.reduce((total, curVal) => total + (curVal ? curVal : 0), 0); },
                    className: 'number',
                    headerClassName: 'number-header',
                }
            ))
        );
        const obj = filteredRawData.reduce((acc, it) => {

            const key = it['FactEvent.timestamp'] + group1.map((x) => { return it[x.fieldName]; }).join(',');
            if(!acc[key]) {
                acc[key] = {
                    date: it['FactEvent.timestamp'],
                    ravenUuid: it['DimRaven.uuid'],
                    cardId: it['DimCard.cardId'],
                    _historyUrlOverride: this.props.historyUrlOverride
                };
                group1.forEach((x) => {
                    acc[key][x.fieldName] = it[x.fieldName] || x.ifNull;
                    acc[key][x.additionalFieldName] = it[x.additionalFieldName] || x.ifNull;
                });
            }
            acc[key][it['DimEventType.name']] = it['FactEvent.count'];
            return acc;
        }, {});

        const rows = Object.values(obj);

        if(!rows || rows.length <= 0)
        {
            return (
                <div>
                    <h4>{strings.reports_no_data_for_selected_time_period}</h4>
                </div>
            );
        }

        const exportURI = this.generateExportURI(columns, rows);

        return (
            <div>
                <ReactTable
                    data = {rows}
                    columns = {columns}
                    noDataText={strings.reports_no_data_for_selected_time_period}
                    showPagination = {false}
                    defaultPageSize = {Number.MAX_SAFE_INTEGER} // RAV-1942, step one (effectively "set the page size to the number of rows" returned from the database)
                    minRows = {0} /* 0, do not show empty filler rows */
                    pivotBy = { [ ...(group1.map((x) => x.fieldName)) ] }
                    getTrProps={(state, rowInfo, column) => {
                        if(rowInfo && rowInfo.aggregated)
                            return { className: '-rollup' };
                        return { className: '-padRow'};
                    }}
                />
                <div className='text-right mt-1'>
                    <a
                        className='btn btn-outline-blue btn-sm'
                        download="event-report.csv"
                        id="export-link"
                        href={exportURI}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {strings.action_export_CSV}
                    </a>
                </div>

                { (rows && rows.length > 0) ? (
                    <div className='mt-1'>
                        <Chart
                            data={this.stackedVehicleEventData(group1, eventNames, rows)}
                            padding='auto'
                            forceFit
                        >
                            <Coord transpose />
                            <Axis name="x" />
                            <Axis name="measure" />
                            <Tooltip />
                            <Legend />
                            <Geom type="intervalStack" position={`x*measure`} color="color" />
                        </Chart>
                    </div>) : null
                }
            </div>
        );
    }

}

const ConnectedEventTable = connect()(EventTable);

ReportsSection.propTypes = {
    selectedDateRange: PropTypes.instanceOf(Array).isRequired
}

export default ReportsSection;

